import React, { useState } from 'react';
import { Device } from '../../lib/types';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

interface MarkRecoveredModalProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
  onConfirm: (device: Device) => Promise<void>;
}

export default function MarkRecoveredModal({ open, onClose, device, onConfirm }: MarkRecoveredModalProps) {
  const [submitting, setSubmitting] = useState(false);

  if (!device) {
    return null;
  }

  const currentDevice = device;

  async function handleConfirm() {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      // Await the mutation so a failure keeps the modal open for retry — the
      // error toast is raised by the handler in Devices, which re-throws.
      await onConfirm(currentDevice);
      onClose();
    } catch {
      // Kept open on failure; toast already surfaced the error.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose}>
      <ModalTitle>Mark device recovered</ModalTitle>
      <p className={styles.modalDescription}>
        You&rsquo;ve received device <b>{device.id}</b>
        {device.petName ? ` (${device.petName})` : ''} back
        {device.adopterName ? ` from ${device.adopterName}` : ''}. It will return to your stock and unlink from the pet
        so you can assign it to a new adoption.
      </p>
      <div className={styles.outcomeBox}>
        <div className={styles.outcomeBoxIcon}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 8l-9-5-9 5v8l9 5 9-5z" />
            <path d="M3 8l9 5 9-5" />
            <path d="M12 13v8" />
          </svg>
        </div>
        <div>
          <div className={styles.outcomeBoxLabel}>What happens</div>
          <div className={styles.outcomeBoxText}>
            Device returns to <b>Devices in Stock</b> &middot; pet cleared &middot; ready to re-transfer.
          </div>
        </div>
      </div>
      <ModalActions>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Working…' : 'Confirm — back to stock'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
